import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading.jsx";
import { useSubscription } from "shared/contexts/Subscription";
import apiCall from "shared/services/ApiAdapter";
import ContentHeaderSection from "../../../shared/common/ContentHeaderSection";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";

/**
 * Form for creating or updating batch details.
 *
 * @param {object} props - The component props.
 * @param {Function} props.navigateTo - Function to navigate to a new page.
 * @param {string} props.batchId - The ID of the batch to update, or null for a new batch.
 * @param {string} props.selectedCourseId - The ID of the course to pre-select for a new batch.
 * @param {string} props.selectedCourseTitle - The name of the course to pre-select for a new batch.
 * @param {boolean} props.useMockData - Flag to use mock data for API calls.
 * @param {Function} props.displayToast - Function to display a toast notification.
 */
const CreateUpdateBatchForm = ({
  navigateTo,
  batchId,
  selectedCourseId,
  selectedCourseTitle,
  useMockData,
  displayToast,
}) => {
  const { t } = useTranslation();
  const { canPerformAction } = useSubscription();
  const { startLoading, stopLoading, isLoading } = useLoading();

  // State to hold the batch data, initialized with default values.
  const [batch, setBatch] = useState({
    batchName: "",
    courseId: "",
    maxCapacity: "",
    location: "Online",
    status: "Active",
  });

  // State for the list of courses fetched from the API.
  const [courses, setCourses] = useState([]);

  // State for the course name to be displayed in the header.
  const [headerCourseName, setHeaderCourseName] = useState(selectedCourseTitle || "");

  // State for form validation errors
  const [errors, setErrors] = useState({});

  // Define static options for location and status
  const locationOptions = ["Online", "Offline"];
  const statusOptions = ["Active", "Inactive"];



  /**
   * Fetches initial data for the form, including the list of courses and,
   * if in update mode, the details of the existing batch.
   */
  const fetchCoursesAndBatch = useCallback(async () => {
    startLoading();
    const userId = getCurrentUserId();
    if (!userId) {
      displayToast(t("userNotLoggedIn"), "error");
      stopLoading();
      return;
    }

    try {
      // Fetch courses filtered by the current user.
      const coursesUrl = useMockData ? "/courses" : `/courses?createdBy.equals=${userId}`;
      const filteredCourses = await apiCall(
        coursesUrl,
        useMockData,
        "GET",
        null,
        () => { },
        () => { },
        displayToast
      );
      setCourses(filteredCourses);

      let initialBatch = {
        batchName: "",
        courseId: selectedCourseId || "",
        maxCapacity: "",
        location: "Online",
        status: "Active",
      };

      // Handle update mode: fetch and populate an existing batch.
      if (batchId) {
        const existingBatch = await apiCall(
          `/batches/${batchId}`,
          useMockData,
          "GET",
          { id: batchId },
          () => { },
          () => { },
          displayToast
        );
        if (existingBatch) {
          initialBatch = {
            ...existingBatch,
            courseId: existingBatch.course?.id || existingBatch.courseId,
            status: existingBatch.isActive ? 'Active' : 'Inactive' // Map isActive to status
          };
        } else {
          displayToast(t("batchNotFound", { batchId }), "error");
          navigateTo("myBatches", selectedCourseTitle, selectedCourseId);
          stopLoading();
          return;
        }
      }

      // Set the batch state with the determined initial values.
      setBatch(initialBatch);

      // Find the corresponding course name for the header.
      const course = filteredCourses.find((c) => c.id === initialBatch.courseId);
      setHeaderCourseName(course ? course.courseName : selectedCourseTitle || "");

    } catch (error) {
      console.error("Failed to load batch details or courses:", error);
      displayToast(t("failedToLoadData"), "error");
    } finally {
      stopLoading();
    }
  }, [batchId, useMockData, startLoading, stopLoading, displayToast, t, navigateTo, selectedCourseId, selectedCourseTitle]);

  // useEffect to fetch initial data on component mount or when batchId changes.
  useEffect(() => {
    fetchCoursesAndBatch();
  }, [fetchCoursesAndBatch]);

  /**
   * Handles changes to form inputs and updates the `batch` state.
   * Also updates the `headerCourseName` when the course selection changes.
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setBatch((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "courseId") {
      const course = courses.find((c) => c.id === value);
      setHeaderCourseName(course ? course.courseName : "");
    }

    // Clear the error for the field being changed
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  /**
   * Validates the form fields before submission.
   * @returns {boolean} True if the form is valid, false otherwise.
   */
  const validateForm = () => {
    const newErrors = {};
    if (!batch.batchName) {
      newErrors.batchName = t("batchNameRequired");
    }
    if (!batch.courseId) {
      newErrors.courseId = t("courseRequired");
    }
    if (!batch.maxCapacity || parseInt(batch.maxCapacity, 10) <= 0) {
      newErrors.maxCapacity = t("capacityRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission for both creating and updating a batch.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    startLoading();

    const userId = getCurrentUserId();
    if (!userId) {
      displayToast(t("userNotLoggedIn"), "error");
      stopLoading();
      return;
    }

    const batchData = {
      ...batch,
      // Convert maxCapacity to an integer.myb
      maxCapacity: parseInt(batch.maxCapacity, 10),
      // Set startDate and endDate to null as per the existing code.
      startDate: null,
      endDate: null,
      // isActive is set based on the status dropdown.
      isActive: batch.status === 'Active',
      // Correctly format the course relationship as a nested object
      course: {
        id: batch.courseId
      }
    };

    try {
      if (!batchId) {
        // --- CREATE NEW BATCH LOGIC ---
        const canCreate = await canPerformAction("maxBatchesPerCourse", 0, batch.courseId);
        if (!canCreate) {
          displayToast(`${t("cannotCreateBatch")} ${t("limitReached")}`, "error");
          stopLoading();
          return;
        }

        const newBatchPayload = {
          ...batchData,
          createdBy: userId,
          createdDate: new Date().toISOString(),
        };

        await apiCall(
          "/batches",
          useMockData,
          "POST",
          newBatchPayload,
          () => { },
          () => { },
          displayToast
        );
        displayToast(t("batchCreatedSuccessfully"), "success");
      } else {
        // --- UPDATE EXISTING BATCH LOGIC ---
        const updatedBatchPayload = {
          ...batchData,
          id: batchId,
          updatedBy: userId,
          updatedDate: new Date().toISOString(),
        };

        await apiCall(
          `/batches/${batchId}`,
          useMockData,
          "PUT",
          updatedBatchPayload,
          () => { },
          () => { },
          displayToast
        );
        displayToast(t("batchUpdatedSuccessfully"), "success");
      }
      navigateTo("myBatches", selectedCourseTitle, selectedCourseId);
    } catch (error) {
      console.error("Failed to save batch:", error);
      displayToast(t("failedToSaveBatch"), "error");
    } finally {
      stopLoading();
    }
  };

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={batchId ? t("updateBatch") : t("createBatch") + " for course " + selectedCourseTitle}
        subtitle={t("createOrUpdateBatch")}
        courseName={headerCourseName} // Use the state variable here
        navigateTo={navigateTo}
      />
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
        {isLoading ? (
          <div className="text-center text-gray-600 dark:text-gray-400">
            {batchId ? t("loadingBatchDetails") : t("loading")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="batchName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("batchName")}
              </label>
              <input
                type="text"
                id="batchName"
                name="batchName"
                value={batch.batchName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
              {errors.batchName && (
                <p className="mt-1 text-sm text-red-600">{errors.batchName}</p>
              )}
            </div>
            <div>
              <label htmlFor="courseId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("course")}
              </label>
              <select
                id="courseId"
                name="courseId"
                value={batch.courseId}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                disabled={isLoading || (!batchId && !!selectedCourseId)}
              >
                <option value="">{t("selectCourse")}</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.courseName}
                  </option>
                ))}
              </select>
              {errors.courseId && (
                <p className="mt-1 text-sm text-red-600">{errors.courseId}</p>
              )}
            </div>
            <div>
              <label htmlFor="maxCapacity" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("batchCapacity")}
              </label>
              <input
                type="number"
                id="maxCapacity"
                name="maxCapacity"
                value={batch.maxCapacity}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
              {errors.maxCapacity && (
                <p className="mt-1 text-sm text-red-600">{errors.maxCapacity}</p>
              )}
            </div>
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("location")}
              </label>
              <select
                id="location"
                name="location"
                value={batch.location}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                disabled={isLoading}
              >
                {locationOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("status")}
              </label>
              <select
                id="status"
                name="status"
                value={batch.status}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                disabled={isLoading}
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigateTo("myBatches", selectedCourseTitle, selectedCourseId)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={isLoading}
              >
                {isLoading
                  ? batchId
                    ? t("updating")
                    : t("creating")
                  : batchId
                    ? t("update")
                    : t("create")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateUpdateBatchForm;